// ── AbcAnalysisCard — Pareto 80/95 ABC 매출 분류 ──
// 매출 누적 80%까지 A(집중 관리), 95%까지 B, 그 외 C(정리 후보)
// + 누적 매출 곡선 시각화 (Pareto 그래프)

import React, { useState, useMemo } from 'react';
import { classifyAbc, paretoCurve, ABC_META } from '../utils/abcAnalysis';

const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');

export default function AbcAnalysisCard({ menus = [], isMobile = false }) {
  const [filter, setFilter] = useState('all');  // 'all' | 'A' | 'B' | 'C'

  const { classified, summary, curve } = useMemo(() => {
    const result = classifyAbc(menus);
    return {
      classified: result.menus,
      summary: result.summary,
      curve: paretoCurve(result.menus),
    };
  }, [menus]);

  if (summary.insufficientData || classified.length === 0) {
    return (
      <div style={{
        background: '#fff', borderRadius: 14, padding: '20px 22px',
        marginBottom: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
      }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#374151', marginBottom: 6 }}>
          🏆 ABC 분석 (Pareto)
        </div>
        <div style={{
          padding: '20px', textAlign: 'center', background: '#f9fafb',
          borderRadius: 8, color: '#9ca3af', fontSize: 13, lineHeight: 1.6,
        }}>
          판매 데이터가 부족해요
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8 }}>
            운영 → 매출 입력에서 <strong>메뉴별 입력 모드</strong>로 판매수량을 등록하면 분석됩니다
          </div>
        </div>
      </div>
    );
  }

  const filtered = filter === 'all'
    ? classified
    : classified.filter(m => m.abcClass === filter);

  // 누적 그래프 좌표
  const W = isMobile ? 320 : 480;
  const H = isMobile ? 140 : 160;
  const padL = 32, padR = 12, padT = 14, padB = 26;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const points = curve.map((p, idx) => ({
    x: padL + (idx / Math.max(curve.length - 1, 1)) * plotW,
    y: padT + plotH - p.cumulativeShare * plotH,
    abcClass: p.abcClass,
    name: p.name,
  }));

  // 80% / 95% 가이드 라인
  const y80 = padT + plotH - 0.80 * plotH;
  const y95 = padT + plotH - 0.95 * plotH;

  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: isMobile ? '14px 12px' : '20px 22px',
      marginBottom: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a1a' }}>
          🏆 ABC 분석 (Pareto 80/95)
        </div>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3, lineHeight: 1.5 }}>
          매출 누적 비율 기준 A·B·C 분류 · 총 매출 {fmt(summary.totalRevenue)}원
        </div>
      </div>

      {/* 통계 칩 */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {(['A', 'B', 'C']).map(cls => {
          const meta = ABC_META[cls];
          const count = summary.counts[cls];
          const rev = summary.revenueByClass[cls];
          const isActive = filter === cls;
          return (
            <button key={cls} type="button"
              onClick={() => setFilter(isActive ? 'all' : cls)}
              style={{
                padding: '6px 12px', borderRadius: 7, fontSize: 11, fontWeight: 700,
                background: isActive ? meta.color : meta.bg,
                color: isActive ? '#fff' : meta.color,
                border: `1px solid ${meta.color}`,
                cursor: 'pointer', textAlign: 'left',
              }}>
              <div>{meta.label} · {count}개</div>
              <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.85 }}>
                {fmt(rev)}원
              </div>
            </button>
          );
        })}
        {filter !== 'all' && (
          <button type="button" onClick={() => setFilter('all')}
            style={{
              padding: '6px 10px', borderRadius: 7, fontSize: 11, fontWeight: 700,
              background: '#fff', color: '#6b7280', border: '1px solid #ddd', cursor: 'pointer',
            }}>전체 보기</button>
        )}
      </div>

      {/* Pareto 누적 곡선 */}
      <div style={{ marginBottom: 14, overflow: 'auto' }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%"
          style={{ maxWidth: W, display: 'block', margin: '0 auto' }}>
          {/* 가이드 라인 */}
          <line x1={padL} y1={y80} x2={W - padR} y2={y80}
            stroke={ABC_META.A.color} strokeWidth="1" strokeDasharray="4,3" opacity="0.4" />
          <line x1={padL} y1={y95} x2={W - padR} y2={y95}
            stroke={ABC_META.B.color} strokeWidth="1" strokeDasharray="4,3" opacity="0.4" />
          <text x={W - padR} y={y80 - 2} textAnchor="end" fontSize="9" fill={ABC_META.A.color}>80%</text>
          <text x={W - padR} y={y95 - 2} textAnchor="end" fontSize="9" fill={ABC_META.B.color}>95%</text>

          {/* 축 */}
          <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="#d1d5db" strokeWidth="1" />
          <line x1={padL} y1={padT} x2={padL} y2={H - padB} stroke="#d1d5db" strokeWidth="1" />

          {/* y축 0/50/100% */}
          {[0, 0.5, 1].map(v => {
            const y = padT + plotH - v * plotH;
            return (
              <g key={v}>
                <line x1={padL - 3} y1={y} x2={padL} y2={y} stroke="#9ca3af" strokeWidth="1" />
                <text x={padL - 5} y={y + 3} textAnchor="end" fontSize="9" fill="#9ca3af">
                  {Math.round(v * 100)}%
                </text>
              </g>
            );
          })}

          {/* 누적 곡선 (line) */}
          {points.length > 1 && (
            <polyline
              points={points.map(p => `${p.x},${p.y}`).join(' ')}
              fill="none" stroke="#FF6F0F" strokeWidth="2" />
          )}

          {/* 점 (작게) */}
          {points.map((p, idx) => (
            <circle key={idx} cx={p.x} cy={p.y} r="2.5"
              fill={ABC_META[p.abcClass].color} stroke="#fff" strokeWidth="1" />
          ))}

          {/* x축 라벨 */}
          <text x={padL} y={H - 8} fontSize="9" fill="#9ca3af">1위</text>
          <text x={W - padR} y={H - 8} textAnchor="end" fontSize="9" fill="#9ca3af">
            {classified.length}위
          </text>
          <text x={W / 2} y={H - 8} textAnchor="middle" fontSize="10" fill="#6b7280">
            메뉴 순위 (매출 ↓)
          </text>
        </svg>
      </div>

      {/* 메뉴 리스트 */}
      <div style={{
        background: '#fafafa', borderRadius: 8, padding: '8px 4px',
        maxHeight: 360, overflowY: 'auto',
      }}>
        {filtered.map(m => {
          const meta = ABC_META[m.abcClass];
          return (
            <div key={m.id} style={{
              padding: '8px 12px', borderBottom: '1px solid #f0f0f0',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <div style={{
                width: 26, height: 26, borderRadius: '50%',
                background: meta.color, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 800, flexShrink: 0,
              }}>{meta.label}</div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  #{m.rank} {m.name}
                </div>
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>
                  매출 {fmt(m.actualSales.revenue)}원 ·
                  {' '}{(m.revenueShare * 100).toFixed(1)}% ·
                  {' '}누적 {(m.cumulativeShare * 100).toFixed(1)}%
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 액션 메시지 */}
      <div style={{
        marginTop: 12, padding: '10px 12px', borderRadius: 8,
        background: '#FAFAFA', border: '1px dashed #D4D4D8',
        fontSize: 11, color: '#78350f', lineHeight: 1.6,
      }}>
        💡 <strong>전체 메뉴 {classified.length}개 중 {summary.counts.A}개(A)가 매출의 80%를 책임집니다.</strong>
        {summary.counts.A > 0 && ` 이 ${summary.counts.A}개는 품질·재고를 최우선으로 관리하세요.`}
        {summary.counts.C > 0 && ` C 등급 ${summary.counts.C}개는 정리 검토 대상입니다.`}
      </div>
    </div>
  );
}
